import React, { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/api";
import { PageHeader, Stat, Card, CardBody, Badge } from "../../components/admin/UI";
import { Users, Trophy, Wallet, CalendarDays, TrendingUp } from "lucide-react";

export default function Dashboard() {
    const { activeClubId, activeSeasonId } = useAuth();
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!activeClubId) return;
        setLoading(true);
        api.get("/dashboard/summary", { params: { club_id: activeClubId, season_id: activeSeasonId || undefined } })
            .then((r) => setSummary(r.data))
            .finally(() => setLoading(false));
    }, [activeClubId, activeSeasonId]);

    const fin = summary?.finance || { total_paid: 0, total_due: 0, remaining: 0 };
    const fmtAr = (n) => `${(n || 0).toLocaleString("fr-FR")} Ar`;

    return (
        <div data-testid="dashboard-page">
            <PageHeader title="Tableau de bord" subtitle="Vue synthétique du club et de la saison active" />

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                <Stat label="Équipes" value={summary?.teams_count ?? "—"} accent="orange" tid="stat-teams" />
                <Stat label="Joueurs" value={summary?.players_count ?? "—"} accent="slate" tid="stat-players" />
                <Stat label="Membres" value={summary?.members_count ?? "—"} accent="blue" tid="stat-members" />
                <Stat label="Matchs joués" value={summary?.matches_played ?? "—"} accent="emerald" tid="stat-matches" />
                <Stat label="Cotisations" value={fmtAr(fin.total_paid)} accent="orange" tid="stat-finance" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
                <Card className="lg:col-span-2">
                    <CardBody>
                        <div className="flex items-center gap-2 mb-4">
                            <CalendarDays className="w-4 h-4 text-orange-500" />
                            <h3 className="font-display font-bold text-slate-900">Prochains matchs</h3>
                        </div>
                        {loading ? (
                            <div className="text-sm text-slate-500">Chargement…</div>
                        ) : summary?.upcoming_matches?.length ? (
                            <div className="space-y-2">
                                {summary.upcoming_matches.map((m) => (
                                    <div key={m.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                                        <div>
                                            <div className="font-semibold text-slate-900">ASSMBB vs {m.opponent}</div>
                                            <div className="text-xs text-slate-500">{m.date} {m.time && `· ${m.time}`} {m.location && `· ${m.location}`}</div>
                                        </div>
                                        <Badge color="orange">À venir</Badge>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-sm text-slate-500">Aucun match programmé.</div>
                        )}
                    </CardBody>
                </Card>

                <Card>
                    <CardBody>
                        <div className="flex items-center gap-2 mb-4">
                            <Wallet className="w-4 h-4 text-orange-500" />
                            <h3 className="font-display font-bold text-slate-900">Finances saison</h3>
                        </div>
                        <div className="space-y-3">
                            <div>
                                <div className="text-xs text-slate-500 uppercase tracking-wider">Encaissé</div>
                                <div className="font-display text-2xl font-black text-emerald-600">{fmtAr(fin.total_paid)}</div>
                            </div>
                            <div>
                                <div className="text-xs text-slate-500 uppercase tracking-wider">À encaisser</div>
                                <div className="font-display text-xl font-bold text-slate-900">{fmtAr(fin.total_due)}</div>
                            </div>
                            <div>
                                <div className="text-xs text-slate-500 uppercase tracking-wider">Restant</div>
                                <div className="font-display text-xl font-bold text-amber-600">{fmtAr(fin.remaining)}</div>
                            </div>
                            <div className="pt-3 border-t border-slate-100 flex items-center gap-2 text-xs text-slate-500">
                                <TrendingUp className="w-3 h-3" />
                                Statuts Mobile Money saisis manuellement (V1)
                            </div>
                        </div>
                    </CardBody>
                </Card>
            </div>

            <Card className="mt-6">
                <CardBody>
                    <div className="flex items-center gap-2 mb-2">
                        <Users className="w-4 h-4 text-orange-500" />
                        <h3 className="font-display font-bold text-slate-900">Démarrage rapide</h3>
                    </div>
                    <p className="text-sm text-slate-500 mb-4">Étapes recommandées pour commencer à utiliser la plateforme :</p>
                    <ol className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 list-decimal pl-5 text-sm">
                        <li className="bg-slate-50 rounded-lg p-3">Créez vos <span className="font-semibold">équipes</span> (U10 à N1A)</li>
                        <li className="bg-slate-50 rounded-lg p-3">Ajoutez vos <span className="font-semibold">joueurs et parents</span></li>
                        <li className="bg-slate-50 rounded-lg p-3">Saisissez les <span className="font-semibold">présences</span> aux entraînements</li>
                        <li className="bg-slate-50 rounded-lg p-3">Programmez les <span className="font-semibold">matchs</span> et entrez les stats</li>
                    </ol>
                </CardBody>
            </Card>
        </div>
    );
}
